import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ValidationFinding } from "@/api/validatorApi";

interface Props {
  anchor: HTMLElement | null;
  findings: ValidationFinding[];
  severity: "error" | "warn";
}

/**
 * Hover-Popover für ein fehlerhaftes EDI-Segment.
 *
 * - Wird per React-Portal an document.body gerendert → entkommt jeglichem
 *   ``overflow: hidden``/``overflow: auto`` der Eltern (Card, TabContainer).
 * - Positionierung via ``getBoundingClientRect``: wenn der Platz oberhalb
 *   des Segments nicht reicht (kleines Fenster, Segment am Viewport-Top),
 *   wird der Popover unterhalb angezeigt. Auch horizontale Clamping gegen
 *   den rechten Viewport-Rand.
 * - ``position: fixed`` → keine Re-Positionierung bei Scroll nötig, weil
 *   das Element beim Mouse-Leave eh verschwindet.
 */
export function SegmentPopover({ anchor, findings, severity }: Props) {
  const [pos, setPos] = useState<{ top: number; left: number; placement: "above" | "below" } | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!anchor) {
      setPos(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    // Höhen-Schätzung: ~26 px pro Finding + 22 px Padding/Border.
    const estHeight = Math.min(360, findings.length * 60 + 24);
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: "above" | "below" =
      spaceAbove >= estHeight + 12 || spaceAbove > spaceBelow ? "above" : "below";
    const top = placement === "above" ? rect.top - estHeight - 8 : rect.bottom + 8;
    // Horizontal: am Segment-Anfang ausrichten, aber innerhalb des Viewports halten.
    const maxLeft = window.innerWidth - 540 - 12;
    const left = Math.max(8, Math.min(rect.left, maxLeft));
    setPos({ top, left, placement });
  }, [anchor, findings.length]);

  if (!anchor || !pos) return null;

  const accentColor =
    severity === "error" ? "var(--sapNegativeColor, #ff5252)" : "var(--sapCriticalColor, #ffb000)";
  const codeColor =
    severity === "error" ? "var(--sapNegativeColor, #b00020)" : "var(--sapCriticalColor, #b08800)";

  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        minWidth: 320,
        maxWidth: 540,
        background: "var(--sapTile_Background, #ffffff)",
        color: "var(--sapTextColor, #32363a)",
        border: "1px solid var(--sapGroup_ContentBorderColor, #cdd3d8)",
        borderTop: `3px solid ${accentColor}`,
        borderRadius: 6,
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.18)",
        padding: "8px 10px",
        fontFamily: "var(--sapFontFamily, '72 Override', '72', Arial, sans-serif)",
        fontSize: "0.78rem",
        lineHeight: 1.4,
        fontWeight: 400,
        textAlign: "left",
        whiteSpace: "normal",
        wordBreak: "normal",
        pointerEvents: "none",
      }}
    >
      {findings.map((f, j) => (
        <div
          key={j}
          style={{
            padding: "4px 0",
            borderBottom:
              j < findings.length - 1
                ? "1px solid var(--sapList_BorderColor, #e5e5e5)"
                : "none",
          }}
        >
          <span
            style={{
              fontFamily: "var(--sapFontMonoFamily, monospace)",
              fontWeight: 700,
              marginRight: 6,
              color: codeColor,
            }}
          >
            [{f.code}]
          </span>
          <span>{f.message_de}</span>
          {f.condition_key && (
            <div
              style={{
                marginTop: 2,
                fontSize: "0.7rem",
                color: "var(--sapContent_LabelColor, #6a6d70)",
                fontFamily: "var(--sapFontMonoFamily, monospace)",
              }}
            >
              Bedingung [{f.condition_key}]
            </div>
          )}
          {f.location.data_element && (
            <div
              style={{
                marginTop: 2,
                fontSize: "0.7rem",
                color: "var(--sapContent_LabelColor, #6a6d70)",
                fontFamily: "var(--sapFontMonoFamily, monospace)",
              }}
            >
              {f.location.segment_tag}#{f.location.segment_index} · DE {f.location.data_element}
              {f.location.value ? ` · "${f.location.value}"` : ""}
            </div>
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}
